import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DefinitionTooltip,
  type DefinitionTooltipInfo,
} from "src/components/common";
import {
  GoogleGroupedBarLegend,
  GROUPED_BAR_COLORS,
  OrganicChartTooltip,
  PIE_COLORS,
  truncateChartLabel,
  type GoogleBarMetricKey,
  type GoogleGroupedChartDatum,
} from "./GoogleAdsChartConfig";
import type { GoogleSectionHelpKey } from "./GoogleAdsTableConfig";
import { formatPaidMediaPercent } from "../../utils/PaidMediaNumberFormat";
import { usePaidMediaChartRtl } from "../../utils/PaidMediaChartRtl";

const SPEND_STACK_THRESHOLD = 5;
const SPEND_LEGEND_TWO_COLUMN_THRESHOLD = 10;

type SpendByCampaignDatum = {
  name: string;
  spend: number;
  spendFormattedValue: string | undefined;
};

interface GoogleChartsRow2Props {
  googleSectionHelp: (_key: GoogleSectionHelpKey) => DefinitionTooltipInfo;
  adTypeChart: GoogleGroupedChartDatum[];
  spendByCampaignChart: SpendByCampaignDatum[];
  clicksConversionsLegendItems: ReadonlyArray<{ key: GoogleBarMetricKey; label: string }>;
  barSeriesLabels: { spend: string; impressions: string; clicks: string; conversions: string; ctr: string };
  formatCountMetric: (_value: number | null | undefined) => string;
  formatConversionMetric: (_value: number | null | undefined) => string;
  formatCurrency: (
    _value: number,
    _options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ) => string;
  numberLocale: string;
}

const GoogleChartsRow2: FC<GoogleChartsRow2Props> = ({
  googleSectionHelp,
  adTypeChart,
  spendByCampaignChart,
  clicksConversionsLegendItems,
  barSeriesLabels,
  formatCountMetric,
  formatConversionMetric,
  formatCurrency,
  numberLocale,
}) => {
  const { t } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();
  const spendItemCount = spendByCampaignChart.length;
  const isStackedLayout = spendItemCount > SPEND_STACK_THRESHOLD;
  const isTwoColumnLegend = spendItemCount > SPEND_LEGEND_TWO_COLUMN_THRESHOLD;

  const spendTotal = useMemo(
    () => spendByCampaignChart.reduce((sum, row) => sum + row.spend, 0),
    [spendByCampaignChart]
  );

  return (
    <div
      className={
        isStackedLayout
          ? "paid-media-google-charts-row2 paid-media-google-charts-row2--stacked"
          : "paid-media-google-charts-row2"
      }
      data-pdf-row="google-charts-secondary"
    >
      {/* Performance by Ad Network — horizontal grouped bar */}
      <article className="paid-media-panel paid-media-panel--chart-stack" data-pdf-block="performance-by-ad-network">
        <div className="paid-media-panel__header paid-media-panel__header--with-legend">
          <div className="paid-media-panel__heading">
            <h3>{t("googleAdsReport.sections.performanceByAdNetwork")}</h3>
            <DefinitionTooltip
              label={t("googleAdsReport.sections.performanceByAdNetwork")}
              info={googleSectionHelp("performanceByAdNetwork")}
              formulaTitle={t("webAnalytics.kpi.formula")}
            />
          </div>
          <GoogleGroupedBarLegend items={clicksConversionsLegendItems} />
        </div>
        <div className="paid-media-chart paid-media-chart--post-type">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={adTypeChart}
              layout="vertical"
              barGap={4}
              barCategoryGap="38%"
              margin={chartRtl.margin({ top: 8, right: 14, bottom: 4, left: 4 })}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e8edf4" horizontal={false} />
              <YAxis
                dataKey="label"
                type="category"
                orientation={chartRtl.yAxisOrientation}
                tickFormatter={value => truncateChartLabel(value, 12)}
                width={110}
                tick={{ fontSize: 11, fill: "#60718b" }}
                axisLine={false}
                tickLine={false}
              />
              <XAxis
                type="number"
                reversed={chartRtl.axisReversed}
                tick={{ fontSize: 11, fill: "#60718b" }}
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
                tickFormatter={value => formatPaidMediaPercent(Number(value), 0)}
              />
              <Tooltip
                content={({ active, label, payload }) => (
                  <OrganicChartTooltip
                    active={active}
                    label={label}
                    labelTitle={t("googleAdsReport.chart.adNetwork")}
                    payload={payload}
                    seriesLabelForDataKey={key => {
                      if (key === "clicksValue") {
                        return barSeriesLabels.clicks;
                      }
                      if (key === "conversionsValue") {
                        return barSeriesLabels.conversions;
                      }
                      return key;
                    }}
                    valueFormatter={(value, name) => {
                      const row = adTypeChart.find(item => item.label === label);
                      if (name === "clicksValue") {
                        return formatCountMetric(row?.clicks ?? Number(value ?? 0));
                      }
                      if (name === "conversionsValue") {
                        return formatConversionMetric(row?.conversions ?? Number(value ?? 0));
                      }
                      return Number(value ?? 0).toLocaleString(numberLocale);
                    }}
                  />
                )}
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar dataKey="clicksValue" fill={GROUPED_BAR_COLORS.clicks} barSize={10} background={{ fill: "#f5f7fb", radius: 5 }} radius={chartRtl.horizontalBarRadius(5)} name="clicksValue" />
              <Bar dataKey="conversionsValue" fill={GROUPED_BAR_COLORS.conversions} barSize={10} background={{ fill: "#f5f7fb", radius: 5 }} radius={chartRtl.horizontalBarRadius(5)} name="conversionsValue" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </article>

      {/* Spend Distribution pie */}
      <article className="paid-media-panel paid-media-panel--chart-stack paid-media-panel--spend-distribution" data-pdf-block="spend-distribution">
        <div className="paid-media-panel__header">
          <div className="paid-media-panel__heading">
            <h3>{t("googleAdsReport.sections.spendDistributionByCampaign")}</h3>
            <DefinitionTooltip
              label={t("googleAdsReport.sections.spendDistributionByCampaign")}
              info={googleSectionHelp("spendDistributionByCampaign")}
              align="end"
              formulaTitle={t("webAnalytics.kpi.formula")}
            />
          </div>
        </div>
        <div
          className={
            isStackedLayout
              ? "paid-media-spend-distribution-body paid-media-spend-distribution-body--wide"
              : "paid-media-spend-distribution-body"
          }
        >
          <div className="paid-media-chart paid-media-chart--roas-stack">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={spendByCampaignChart}
                  dataKey="spend"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  innerRadius={60}
                  strokeWidth={0}
                >
                  {spendByCampaignChart.map((row, index) => (
                    <Cell key={`${row.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    // Campaign names can repeat (same name, different IDs), so read
                    // the hovered slice from the payload instead of matching by name.
                    const row = payload?.[0]?.payload as SpendByCampaignDatum | undefined;
                    return (
                      <OrganicChartTooltip
                        active={active}
                        label={String(payload?.[0]?.name ?? "")}
                        labelTitle={t("googleAdsReport.chart.campaign")}
                        payload={payload}
                        valueFormatter={(value, _name) =>
                          row?.spendFormattedValue ?? formatCurrency(Number(value ?? 0))
                        }
                      />
                    );
                  }}
                  wrapperStyle={{ pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div
            className={
              isTwoColumnLegend
                ? "paid-media-pie-legend paid-media-pie-legend--two-columns"
                : "paid-media-pie-legend"
            }
          >
            {spendByCampaignChart.map((row, index) => {
              const pct = formatPaidMediaPercent(
                spendTotal > 0 ? (row.spend / spendTotal) * 100 : 0
              );
              return (
                <div key={`${row.name}-${index}`} className="paid-media-pie-legend__item">
                  <span
                    className="paid-media-pie-legend__swatch"
                    style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                  />
                  <span className="paid-media-pie-legend__name">{row.name}</span>
                  <span className="paid-media-pie-legend__value">
                    {row.spendFormattedValue ?? formatCurrency(row.spend)}
                    <span className="paid-media-pie-legend__pct">({pct})</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </article>
    </div>
  );
};

export default GoogleChartsRow2;
